import React, { useState } from 'react';
import { ArrowLeftIcon, MapIcon, MapPinIcon, DownloadIcon, AlertTriangleIcon, CheckCircle2Icon, FileTextIcon, UploadIcon, FileIcon } from 'lucide-react';
import { ownerData } from '../data/sampleData';
import { Modal } from '../components/Modal';
interface LandDetailsPageProps {
  landId: string | null;
  navigate: (page: string) => void;
  t: (key: string) => string;
  lands: any[];
}
export function LandDetailsPage({
  landId,
  navigate,
  t,
  lands
}: LandDetailsPageProps) {
  const [activeTab, setActiveTab] = useState('info');
  const [isMapModalOpen, setIsMapModalOpen] = useState(false);
  const [documents, setDocuments] = useState<{
    name: string;
    date: string;
  }[]>([{
    name: 'Original Deed.pdf',
    date: '12 May 2015'
  }]);
  const land = lands.find(l => l.id === landId) || lands[0];
  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const newDoc = {
        name: e.target.files[0].name,
        date: new Date().toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        })
      };
      setDocuments([...documents, newDoc]);
    }
  };
  return <div className="space-y-6 max-w-5xl mx-auto pb-20 lg:pb-6" data-id="element-311">
      <div className="flex items-center justify-between" data-id="element-312">
        <button onClick={() => navigate('mylands')} className="flex items-center gap-2 text-slate-500 hover:text-slate-800 transition-colors" data-id="element-313">
          <ArrowLeftIcon className="w-4 h-4" data-id="element-314" /> {t('common.back')}
        </button>
        <div className="flex gap-2 sm:gap-3" data-id="element-315">
          <button onClick={() => setIsMapModalOpen(true)} className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 px-3 sm:px-4 py-2 rounded-lg font-medium text-sm transition-colors shadow-sm flex items-center gap-2" data-id="element-316">
            <MapIcon className="w-4 h-4" data-id="element-317" />{' '}
            <span className="hidden sm:inline" data-id="element-318">{t('details.viewMap')}</span>
          </button>
          <button onClick={() => alert('Downloading land record...')} className="bg-[#006A4E] text-white hover:bg-[#00523c] px-3 sm:px-4 py-2 rounded-lg font-medium text-sm transition-colors shadow-sm flex items-center gap-2" data-id="element-319">
            <DownloadIcon className="w-4 h-4" data-id="element-320" />{' '}
            <span className="hidden sm:inline" data-id="element-321">{t('common.download')}</span>
          </button>
        </div>
      </div>

      {/* Header Card */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6" data-id="element-322">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6" data-id="element-323">
          <div data-id="element-324">
            <div className="flex items-center gap-3 mb-2" data-id="element-325">
              <h2 className="text-2xl font-bold text-slate-900" data-id="element-326">{land.id}</h2>
              <span className="px-3 py-1 bg-slate-100 text-slate-700 text-xs font-medium rounded-full" data-id="element-327">
                {t(`land.${land.type.toLowerCase()}`) !== `land.${land.type.toLowerCase()}` ? t(`land.${land.type.toLowerCase()}`) : land.type}
              </span>
            </div>
            <p className="text-slate-500 flex items-center gap-2 text-sm" data-id="element-328">
              <MapIcon className="w-4 h-4 shrink-0" data-id="element-329" /> {land.mouza},{' '}
              {land.upazila}, {land.district}
            </p>
          </div>

          <div className="flex flex-wrap gap-2" data-id="element-330">
            {land.tax === 'Due' && <span className="flex items-center gap-1 px-3 py-1.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-lg text-sm font-medium" data-id="element-331">
                <AlertTriangleIcon className="w-4 h-4" data-id="element-332" /> {t('status.due')}
              </span>}
            {land.dispute !== 'None' && <span className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-lg text-sm font-medium" data-id="element-333">
                <AlertTriangleIcon className="w-4 h-4" data-id="element-334" /> {t('land.dispute')}
              </span>}
            {land.tax === 'Paid' && land.mutation === 'Completed' && land.dispute === 'None' && <span className="flex items-center gap-1 px-3 py-1.5 bg-green-50 text-green-700 border border-green-200 rounded-lg text-sm font-medium" data-id="element-335">
                  <CheckCircle2Icon className="w-4 h-4" data-id="element-336" /> All Clear
                </span>}
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6 pt-6 border-t border-slate-100" data-id="element-337">
          <div data-id="element-338">
            <p className="text-sm text-slate-500 mb-1" data-id="element-339">{t('land.khatian')}</p>
            <p className="font-bold text-slate-800" data-id="element-340">{land.khatian}</p>
          </div>
          <div data-id="element-341">
            <p className="text-sm text-slate-500 mb-1" data-id="element-342">{t('land.dag')}</p>
            <p className="font-bold text-slate-800" data-id="element-343">{land.plot}</p>
          </div>
          <div data-id="element-344">
            <p className="text-sm text-slate-500 mb-1" data-id="element-345">{t('land.area')}</p>
            <p className="font-bold text-slate-800" data-id="element-346">{land.area}</p>
          </div>
          <div data-id="element-347">
            <p className="text-sm text-slate-500 mb-1" data-id="element-348">{t('land.ownership')}</p>
            <p className="font-bold text-slate-800" data-id="element-349">
              {land.ownership === 'Sole' ? 'একক' : 'যৌথ'}
            </p>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden" data-id="element-350">
        <div className="flex overflow-x-auto border-b border-slate-200 scrollbar-hide" data-id="element-351">
          {['info', 'history', 'tax', 'documents'].map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`px-4 sm:px-6 py-4 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === tab ? 'border-[#006A4E] text-[#006A4E]' : 'border-transparent text-slate-500 hover:text-slate-700 hover:bg-slate-50'}`} data-id="element-352">
              {tab === 'info' && t('details.info')}
              {tab === 'history' && t('details.history')}
              {tab === 'tax' && t('details.tax')}
              {tab === 'documents' && t('details.documents')}
            </button>)}
        </div>

        <div className="p-4 sm:p-6" data-id="element-353">
          {activeTab === 'info' && <div className="space-y-8" data-id="element-354">
              <div data-id="element-355">
                <h3 className="text-lg font-bold text-slate-800 mb-4" data-id="element-356">
                  {t('land.ownership')}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 bg-slate-50 p-4 rounded-lg border border-slate-100" data-id="element-357">
                  <div data-id="element-358">
                    <p className="text-sm text-slate-500" data-id="element-359">
                      {t('certificate.ownerName')}
                    </p>
                    <p className="font-medium text-slate-900" data-id="element-360">
                      {ownerData.name}
                    </p>
                  </div>
                  <div data-id="element-361">
                    <p className="text-sm text-slate-500" data-id="element-362">Ownership Share</p>
                    <p className="font-medium text-slate-900" data-id="element-363">
                      {land.ownership === 'Sole' ? '100%' : '50%'}
                    </p>
                  </div>
                </div>
              </div>

              {land.dispute !== 'None' && <div data-id="element-364">
                  <h3 className="text-lg font-bold text-red-700 mb-4 flex items-center gap-2" data-id="element-365">
                    <AlertTriangleIcon className="w-5 h-5" data-id="element-366" /> Case/Dispute
                    Information
                  </h3>
                  <div className="bg-red-50 p-4 rounded-lg border border-red-100" data-id="element-367">
                    <p className="font-medium text-red-900 mb-2" data-id="element-368">
                      {land.dispute}
                    </p>
                    <p className="text-sm text-red-700" data-id="element-369">
                      Case No: CV-2025-8902
                    </p>
                    <p className="text-sm text-red-700 mt-2" data-id="element-370">
                      Next Hearing: 20 March 2026
                    </p>
                  </div>
                </div>}
            </div>}

          {activeTab === 'history' && <div className="relative border-l-2 border-slate-200 ml-3 space-y-8 pb-4" data-id="element-371">
              <div className="relative pl-6" data-id="element-372">
                <div className="absolute w-4 h-4 bg-[#006A4E] rounded-full -left-[9px] top-1 border-4 border-white" data-id="element-373"></div>
                <p className="text-sm text-slate-500 mb-1" data-id="element-374">15 Jan 2026</p>
                <h4 className="font-bold text-slate-800" data-id="element-375">
                  Digital Record Updated
                </h4>
                <p className="text-sm text-slate-600 mt-1" data-id="element-376">
                  Land record digitized and linked to Unified Account.
                </p>
              </div>
              <div className="relative pl-6" data-id="element-377">
                <div className="absolute w-4 h-4 bg-slate-300 rounded-full -left-[9px] top-1 border-4 border-white" data-id="element-378"></div>
                <p className="text-sm text-slate-500 mb-1" data-id="element-379">12 May 2015</p>
                <h4 className="font-bold text-slate-800" data-id="element-380">Mutation Completed</h4>
                <p className="text-sm text-slate-600 mt-1" data-id="element-381">
                  Ownership transferred to {ownerData.name}.
                </p>
              </div>
            </div>}

          {activeTab === 'tax' && <div data-id="element-382">
              <div className="flex justify-between items-center mb-4" data-id="element-383">
                <h3 className="text-lg font-bold text-slate-800" data-id="element-384">
                  {t('details.tax')}
                </h3>
                {land.tax === 'Due' && <button onClick={() => navigate('services')} className="bg-[#006A4E] text-white px-4 py-2 rounded-lg text-sm font-medium" data-id="element-385">
                    Pay Current Tax
                  </button>}
              </div>
              <div className="overflow-x-auto" data-id="element-386">
                <table className="w-full text-left text-sm min-w-[400px]" data-id="element-387">
                  <thead className="bg-slate-50 text-slate-600" data-id="element-388">
                    <tr data-id="element-389">
                      <th className="py-3 px-4 rounded-l-lg" data-id="element-390">Fiscal Year</th>
                      <th className="py-3 px-4" data-id="element-391">Amount</th>
                      <th className="py-3 px-4" data-id="element-392">Status</th>
                      <th className="py-3 px-4 rounded-r-lg" data-id="element-393">Payment Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100" data-id="element-394">
                    <tr data-id="element-395">
                      <td className="py-3 px-4 font-medium" data-id="element-396">2025-2026</td>
                      <td className="py-3 px-4" data-id="element-397">৳ 500</td>
                      <td className="py-3 px-4" data-id="element-398">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${land.tax === 'Paid' ? 'bg-green-100 text-green-800' : 'bg-amber-100 text-amber-800'}`} data-id="element-399">
                          {t(`status.${land.tax.toLowerCase()}`) !== `status.${land.tax.toLowerCase()}` ? t(`status.${land.tax.toLowerCase()}`) : land.tax}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-slate-500" data-id="element-400">
                        {land.tax === 'Paid' ? '10 Jan 2026' : '-'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>}

          {activeTab === 'documents' && <div className="space-y-6" data-id="element-401">
              <div className="flex justify-between items-center" data-id="element-402">
                <h3 className="text-lg font-bold text-slate-800" data-id="element-403">
                  {t('details.documents')}
                </h3>
                <div data-id="element-404">
                  <input type="file" id="doc-upload" className="hidden" onChange={handleFileUpload} data-id="element-405" />
                  <label htmlFor="doc-upload" className="bg-white border border-slate-300 text-slate-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-slate-50 cursor-pointer flex items-center gap-2" data-id="element-406">
                    <UploadIcon className="w-4 h-4" data-id="element-407" /> {t('details.uploadDoc')}
                  </label>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-408">
                {documents.map((doc, idx) => <div key={idx} className="flex items-center gap-3 p-3 border border-slate-200 rounded-lg bg-slate-50" data-id="element-409">
                    <div className="w-10 h-10 bg-blue-100 text-blue-600 rounded flex items-center justify-center shrink-0" data-id="element-410">
                      <FileIcon className="w-5 h-5" data-id="element-411" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-412">
                      <p className="font-medium text-slate-800 text-sm truncate" data-id="element-413">
                        {doc.name}
                      </p>
                      <p className="text-xs text-slate-500" data-id="element-414">{doc.date}</p>
                    </div>
                    <button className="text-slate-400 hover:text-[#006A4E] p-2" data-id="element-415">
                      <DownloadIcon className="w-4 h-4" data-id="element-416" />
                    </button>
                  </div>)}
              </div>
            </div>}
        </div>
      </div>

      {/* Map Modal */}
      <Modal isOpen={isMapModalOpen} onClose={() => setIsMapModalOpen(false)} title={`${t('dashboard.mapView')} - ${t('land.dag')} ${land.plot}`} data-id="element-417">
        <div className="w-full h-64 bg-slate-100 rounded-lg border border-slate-200 flex flex-col items-center justify-center relative overflow-hidden" data-id="element-418">
          <div className="absolute inset-0 opacity-10" style={{
          backgroundImage: 'radial-gradient(#000 1px, transparent 1px)',
          backgroundSize: '20px 20px'
        }} data-id="element-419"></div>
          <MapPinIcon className="w-10 h-10 text-[#C8102E] relative z-10 mb-2 drop-shadow-md" data-id="element-420" />
          <div className="bg-white px-3 py-1.5 rounded shadow-sm relative z-10 text-sm font-bold text-slate-800" data-id="element-421">
            {t('land.dag')} {land.plot}
          </div>
          <p className="text-xs text-slate-500 mt-2 relative z-10" data-id="element-422">
            Coordinates: 23.9382° N, 90.5636° E
          </p>
        </div>
      </Modal>
    </div>;
}